import { ReactElement } from 'react';
import { Avatar, Group, Menu, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconLogout, IconSelector, IconUser } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '@workos-inc/authkit-react';
import { User } from '~api';
import { useProfile } from '~portal/api';
import { Cmp, isStandalone, logger, useSignOut } from '~portal/lib';
import * as styles from './AccountMenu.css';

/**
 * @desc The signed-in account widget: avatar, display name and, on the second line, the user's
 * WorkOS organisation name. Clicking it opens the account menu — the app's sign-out affordance
 * on both build targets.
 *
 * The design shows a "Free Plan" line, but the app has no plan concept — the organisation name is
 * the closest true fact WorkOS carries. Membership is org-less only for an invitation the target
 * hasn't been attached to an organisation for yet (an admin action, see `workos.gateway.ts`), so
 * this falls back to the signed-in user's access tier, derived from their role claims, only in
 * that narrow window.
 */
const tierLabel = (roles: readonly string[]): string => {
  if (roles.includes(User.roles.InternalAdmin) || roles.includes(User.roles.InternalUser)) {
    return 'Internal';
  }
  if (roles.includes(User.roles.PlatformPremium)) return 'Premium';
  if (roles.includes(User.roles.PlatformBasic)) return 'Basic';
  if (roles.includes(User.roles.AgentBeta) || roles.includes(User.roles.PlatformBeta)) {
    return 'Beta';
  }
  return 'Pending';
};

export type AccountMenuProps = {
  /**
   * Where the block is rendered: a compact header row (`nav`, the default) or a sidebar's pinned
   * footer (`sidebar`), which gets the full rail width and opens its menu upwards.
   */
  placement?: 'nav' | 'sidebar';
  /**
   * A custom trigger in place of the built-in avatar block — the platform's global-nav account
   * block (`GlobalNav/AccountBlock`) draws its own Figma row but opens this same menu, so the
   * Account / Sign out items live in one place. Must be a single element that forwards its ref
   * (a Mantine `UnstyledButton`, say), as `Menu.Target` requires.
   */
  target?: ReactElement;
};

/**
 * @desc The signed-in account's display name and email, resolved the same way everywhere an
 * account block is drawn: the portal profile first, then the AuthKit user, then the email.
 */
export function useAccountIdentity() {
  const { user } = useAuth();
  const profile = useProfile();

  const name =
    profile?.name.formatted ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    profile?.email ||
    user?.email ||
    'Signed in';

  return { name, email: profile?.email || user?.email };
}

export const AccountMenu: Cmp<AccountMenuProps> = function ({ placement = 'nav', target }) {
  const { user, roles } = useAuth();
  const profile = useProfile();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const { name, email } = useAccountIdentity();

  const openAccount = () => {
    navigate({ to: '/account' }).catch((err) => {
      logger.error('[AccountMenu] Navigation failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  const secondLine = profile?.organisationName || tierLabel(roles ?? []);

  return (
    <Menu position={placement === 'sidebar' ? 'top-start' : 'bottom-end'} offset={6} withinPortal>
      <Menu.Target>
        {target ?? (
          <UnstyledButton
            aria-label="Account menu"
            className={placement === 'sidebar' ? styles.sidebarAccount : styles.navAccount}
          >
            <Group gap="sm" wrap="nowrap" p="xs">
              <Avatar
                src={user?.profilePictureUrl ?? undefined}
                alt={name}
                name={name}
                color="blue"
                radius="sm"
                size={30}
              />
              <Stack
                gap={0}
                miw={0}
                flex={1}
                className={placement === 'nav' ? styles.navDetails : undefined}
              >
                <Text size="sm" fw={500} lh={1.3} className={styles.line}>
                  {name}
                </Text>
                <Text size="xs" fw={500} lh={1.3} c="dimmed" className={styles.line}>
                  {secondLine}
                </Text>
              </Stack>
              <IconSelector
                size={16}
                className={
                  placement === 'nav' ? `${styles.chevron} ${styles.navDetails}` : styles.chevron
                }
              />
            </Group>
          </UnstyledButton>
        )}
      </Menu.Target>
      <Menu.Dropdown>
        {email && <Menu.Label className={styles.line}>{email}</Menu.Label>}
        {/* The account page is platform-only; the standalone chat build has nowhere to send it. */}
        {!isStandalone && (
          <Menu.Item leftSection={<IconUser size={16} />} onClick={openAccount}>
            Account
          </Menu.Item>
        )}
        <Menu.Item leftSection={<IconLogout size={16} />} onClick={signOut}>
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
